import { useState, useEffect } from 'react'
import {Search, User, ArrowLeft} from 'lucide-react'
import { lumi } from '../lib/lumi'

interface UserProfile {
  _id: string
  userId: string
  nickname: string
  bio?: string
  profileImageUrl?: string
  bannerImageUrl?: string
  xUrl?: string
  baseAppUrl?: string
  farcasterUrl?: string
}

interface SearchUsersPageProps {
  onBack: () => void
  onUserClick: (userId: string) => void
}

export function SearchUsersPage({ onBack, onUserClick }: SearchUsersPageProps) {
  const [users, setUsers] = useState<UserProfile[]>([])
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredUsers(users)
    } else {
      const query = searchQuery.toLowerCase()
      const filtered = users.filter(user => 
        user.nickname?.toLowerCase().includes(query) ||
        user.bio?.toLowerCase().includes(query)
      )
      setFilteredUsers(filtered)
    }
  }, [searchQuery, users])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const result = await lumi.entities.userProfiles.list({
        sort: { createdAt: -1 },
        limit: 100
      })
      
      if (result?.list) {
        setUsers(result.list)
        setFilteredUsers(result.list)
      }
    } catch (error) {
      console.error('Failed to fetch users:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back
          </button>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Search Users
          </h1>
          <p className="text-xl text-gray-400">
            Discover and connect with community members
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or bio..."
              className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-400">
            {loading ? 'Loading...' : `${filteredUsers.length} user${filteredUsers.length !== 1 ? 's' : ''} found`}
          </p>
        </div>

        {/* Users Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="text-gray-400 text-xl">Loading users...</div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-20">
            <User className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-xl">
              {searchQuery ? 'No users found matching your search' : 'No users yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map((user) => (
              <div
                key={user._id}
                onClick={() => onUserClick(user.userId)}
                className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-blue-500 hover:shadow-[0_0_20px_rgba(0,82,255,0.3)] transition-all cursor-pointer group"
              >
                {/* Banner */}
                <div className="relative h-32 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
                  {user.bannerImageUrl ? (
                    <img 
                      src={user.bannerImageUrl} 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-500/20 to-purple-500/20" />
                  )}
                  
                  {/* Profile Image Overlay */}
                  <div className="absolute -bottom-12 left-6">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-slate-800 border-4 border-[#0A0E27]">
                      {user.profileImageUrl ? (
                        <img 
                          src={user.profileImageUrl} 
                          alt={user.nickname} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-10 h-10 text-gray-500" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* User Info */}
                <div className="pt-16 pb-6 px-6">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                    {user.nickname || 'Anonymous User'}
                  </h3>
                  
                  {user.bio && (
                    <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                      {user.bio}
                    </p>
                  )}

                  {/* Social Links */}
                  {(user.xUrl || user.baseAppUrl || user.farcasterUrl) && (
                    <div className="flex flex-wrap gap-2">
                      {user.xUrl && (
                        <span className="px-2 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs rounded">
                          X
                        </span>
                      )}
                      {user.baseAppUrl && (
                        <span className="px-2 py-1 bg-purple-500/10 border border-purple-500/30 text-purple-400 text-xs rounded">
                          Base
                        </span>
                      )}
                      {user.farcasterUrl && (
                        <span className="px-2 py-1 bg-green-500/10 border border-green-500/30 text-green-400 text-xs rounded">
                          Farcaster
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
